'use client';
import React, { useState, useRef } from 'react';

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

interface ParsedRange {
  valid: boolean;
  pages: number[];
  error?: string;
}

function parseRanges(input: string, total: number): ParsedRange {
  if (!input.trim()) return { valid: false, pages: [], error: 'Please enter a page range.' };

  const pages: number[] = [];
  const parts = input.split(',').map((s) => s.trim()).filter(Boolean);

  for (const part of parts) {
    if (part.includes('-')) {
      const segments = part.split('-');
      if (segments.length !== 2) return { valid: false, pages: [], error: `Invalid range: "${part}"` };
      const start = parseInt(segments[0], 10);
      const end = parseInt(segments[1], 10);
      if (isNaN(start) || isNaN(end)) return { valid: false, pages: [], error: `Invalid range: "${part}"` };
      if (start < 1) return { valid: false, pages: [], error: `Page numbers must be 1 or greater (got ${start}).` };
      if (end > total) return { valid: false, pages: [], error: `Page ${end} exceeds total page count (${total}).` };
      if (start > end) return { valid: false, pages: [], error: `Range "${part}" is invalid — start must be ≤ end.` };
      for (let i = start; i <= end; i++) pages.push(i - 1);
    } else {
      const n = parseInt(part, 10);
      if (isNaN(n)) return { valid: false, pages: [], error: `"${part}" is not a valid page number.` };
      if (n < 1) return { valid: false, pages: [], error: `Page numbers must be 1 or greater (got ${n}).` };
      if (n > total) return { valid: false, pages: [], error: `Page ${n} exceeds total page count (${total}).` };
      pages.push(n - 1);
    }
  }

  const unique = [...new Set(pages)].sort((a, b) => a - b);
  if (unique.length === 0) return { valid: false, pages: [], error: 'No valid pages found in the specified range.' };
  return { valid: true, pages: unique };
}

export default function SplitPDFTool() {
  const [file, setFile] = useState<File | null>(null);
  const [pageCount, setPageCount] = useState(0);
  const [loadingFile, setLoadingFile] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const [ranges, setRanges] = useState('');
  const [rangeError, setRangeError] = useState<string | null>(null);
  const [processing, setProcessing] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [result, setResult] = useState<{ size: number; pageCount: number; url: string; filename: string } | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const loadPDF = async (f: File) => {
    if (f.type !== 'application/pdf') {
      setFileError('Only PDF files are supported. Please upload a .pdf file.');
      return;
    }
    setFileError(null);
    setResult(null);
    setRangeError(null);
    setLoadingFile(true);
    setFile(f);
    try {
      const { PDFDocument } = await import('pdf-lib');
      const buf = await f.arrayBuffer();
      const pdf = await PDFDocument.load(buf);
      const count = pdf.getPageCount();
      setPageCount(count);
      setRanges(`1-${count}`);
    } catch {
      setFileError('Could not read this PDF. It may be corrupted or password-protected.');
      setFile(null);
      setPageCount(0);
    } finally {
      setLoadingFile(false);
    }
  };

  const handleRangeChange = (value: string) => {
    setRanges(value);
    setRangeError(null);
    setResult(null);
  };

  const getPreviewPages = (): number[] => {
    if (!pageCount || !ranges.trim()) return [];
    const parsed = parseRanges(ranges, pageCount);
    return parsed.valid ? parsed.pages.map((p) => p + 1) : [];
  };

  const previewPages = getPreviewPages();

  const handleSplit = async () => {
    if (!file || !pageCount) return;

    const parsed = parseRanges(ranges, pageCount);
    if (!parsed.valid) {
      setRangeError(parsed.error ?? 'Invalid page range.');
      return;
    }

    setRangeError(null);
    setResult(null);
    setProcessing(true);

    try {
      const { PDFDocument } = await import('pdf-lib');
      const buf = await file.arrayBuffer();
      const srcPdf = await PDFDocument.load(buf);

      const newPdf = await PDFDocument.create();
      const copied = await newPdf.copyPages(srcPdf, parsed.pages);
      copied.forEach((p) => newPdf.addPage(p));
      const bytes = await newPdf.save();

      const blob = new Blob([bytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const safeRanges = ranges.replace(/\s/g, '').replace(/,/g, '_');
      const filename = `split_pages_${safeRanges}.pdf`;
      setResult({ size: blob.size, pageCount: newPdf.getPageCount(), url, filename });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to split PDF. Please try again.';
      setRangeError(msg);
    } finally {
      setProcessing(false);
    }
  };

  const handleDownload = () => {
    if (!result) return;
    const a = document.createElement('a');
    a.href = result.url;
    a.download = result.filename;
    a.click();
  };

  const handleReset = () => {
    if (result?.url) URL.revokeObjectURL(result.url);
    setFile(null);
    setPageCount(0);
    setRanges('');
    setResult(null);
    setFileError(null);
    setRangeError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  return (
    <div className="space-y-5">
      {/* File error */}
      {fileError && (
        <div className="flex items-start gap-3 bg-red-50 border border-red-100 rounded-xl px-4 py-3">
          <svg className="w-4 h-4 text-red-500 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
          </svg>
          <p className="text-red-700 text-sm flex-1">{fileError}</p>
          <button onClick={() => setFileError(null)} className="text-red-400 hover:text-red-600 transition-colors">
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}

      {/* Upload zone */}
      {!file && !loadingFile && (
        <div
          className={`upload-zone rounded-2xl p-10 text-center cursor-pointer transition-all ${isDragging ? 'drag-over' : ''}`}
          onClick={() => fileInputRef.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setIsDragging(false);
            const f = e.dataTransfer.files?.[0];
            if (f) loadPDF(f);
          }}
        >
          <div className="w-12 h-12 rounded-2xl bg-red-50 flex items-center justify-center mx-auto mb-3">
            <svg className="w-6 h-6 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
            </svg>
          </div>
          <p className="font-semibold text-text-primary mb-1">Upload PDF</p>
          <p className="text-text-muted text-sm">Drag & drop or click to browse</p>
          <input
            ref={fileInputRef}
            type="file"
            accept="application/pdf"
            className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; if (f) loadPDF(f); }}
          />
        </div>
      )}

      {/* Loading file state */}
      {loadingFile && (
        <div className="upload-zone rounded-2xl p-10 text-center">
          <div className="w-10 h-10 mx-auto mb-3 flex items-center justify-center">
            <svg className="w-8 h-8 text-brand-blue animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          </div>
          <p className="text-text-secondary text-sm font-medium">Reading PDF…</p>
        </div>
      )}

      {/* File loaded — settings */}
      {file && !loadingFile && (
        <div className="space-y-4">
          {/* File info card */}
          <div className="flex items-center gap-3 bg-white rounded-xl px-4 py-3 border border-gray-100">
            <div className="w-9 h-10 rounded-lg bg-red-50 border border-red-100 flex items-center justify-center flex-shrink-0">
              <span className="text-[8px] font-black text-red-500">PDF</span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-text-primary truncate">{file.name}</p>
              <p className="text-xs text-text-muted">{formatBytes(file.size)}</p>
            </div>
            {/* Page count badge */}
            <div className="flex items-center gap-1.5 bg-blue-50 rounded-lg px-3 py-1.5 flex-shrink-0">
              <svg className="w-3.5 h-3.5 text-brand-blue" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
              </svg>
              <span className="text-brand-blue font-bold text-sm">{pageCount} pages</span>
            </div>
            <button
              onClick={handleReset}
              className="text-text-muted hover:text-red-500 transition-colors text-xs ml-1"
            >
              Remove
            </button>
          </div>

          {/* Page range input */}
          <div>
            <label className="block font-semibold text-text-primary text-sm mb-1.5">
              Page Range
            </label>
            <p className="text-xs text-text-muted mb-2">
              Enter pages to extract. Examples: <code className="bg-gray-100 px-1 py-0.5 rounded text-xs">1-3</code>{' '}
              <code className="bg-gray-100 px-1 py-0.5 rounded text-xs">1,3,5</code>{' '}
              <code className="bg-gray-100 px-1 py-0.5 rounded text-xs">1-3,5,8-10</code>
            </p>
            <input
              type="text"
              value={ranges}
              onChange={(e) => handleRangeChange(e.target.value)}
              placeholder={`e.g. 1-3, 5, 7-${pageCount}`}
              className={`w-full px-4 py-3 rounded-xl border text-text-primary text-sm focus:outline-none focus:ring-2 transition-colors ${
                rangeError
                  ? 'border-red-300 focus:ring-red-200 focus:border-red-400 bg-red-50' :'border-gray-200 focus:ring-brand-blue/30 focus:border-brand-blue'
              }`}
            />
            {rangeError && (
              <p className="text-red-600 text-xs mt-1.5 flex items-center gap-1">
                <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
                </svg>
                {rangeError}
              </p>
            )}
          </div>

          {/* Page preview chips */}
          {previewPages.length > 0 && !rangeError && (
            <div>
              <p className="text-xs font-medium text-text-secondary mb-2">
                {previewPages.length} page{previewPages.length !== 1 ? 's' : ''} selected:
              </p>
              <div className="flex flex-wrap gap-1.5">
                {previewPages.slice(0, 30).map((p) => (
                  <span
                    key={p}
                    className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-blue-50 text-brand-blue text-xs font-semibold border border-blue-100"
                  >
                    {p}
                  </span>
                ))}
                {previewPages.length > 30 && (
                  <span className="inline-flex items-center px-2 h-8 rounded-lg bg-gray-100 text-text-muted text-xs">
                    +{previewPages.length - 30} more
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Quick range presets */}
          <div>
            <p className="text-xs font-medium text-text-secondary mb-2">Quick select:</p>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handleRangeChange(`1-${pageCount}`)}
                className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 hover:border-brand-blue hover:text-brand-blue transition-colors text-text-secondary"
              >
                All pages
              </button>
              {pageCount >= 2 && (
                <button
                  onClick={() => handleRangeChange(`1-${Math.floor(pageCount / 2)}`)}
                  className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 hover:border-brand-blue hover:text-brand-blue transition-colors text-text-secondary"
                >
                  First half
                </button>
              )}
              {pageCount >= 2 && (
                <button
                  onClick={() => handleRangeChange(`${Math.floor(pageCount / 2) + 1}-${pageCount}`)}
                  className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 hover:border-brand-blue hover:text-brand-blue transition-colors text-text-secondary"
                >
                  Second half
                </button>
              )}
              {pageCount >= 1 && (
                <button
                  onClick={() => handleRangeChange('1')}
                  className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 hover:border-brand-blue hover:text-brand-blue transition-colors text-text-secondary"
                >
                  First page only
                </button>
              )}
            </div>
          </div>

          {/* Success result */}
          {result && (
            <div className="bg-green-50 border border-green-100 rounded-2xl p-4 space-y-3">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                  <svg className="w-3.5 h-3.5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                  </svg>
                </div>
                <p className="font-semibold text-green-800 text-sm">Split complete!</p>
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="bg-white rounded-xl p-3 text-center">
                  <p className="text-text-muted text-xs mb-1">Pages extracted</p>
                  <p className="font-bold text-text-primary">{result.pageCount}</p>
                </div>
                <div className="bg-white rounded-xl p-3 text-center">
                  <p className="text-text-muted text-xs mb-1">File size</p>
                  <p className="font-bold text-brand-blue">{formatBytes(result.size)}</p>
                </div>
              </div>
              <button
                onClick={handleDownload}
                className="btn-primary w-full py-3 flex items-center justify-center gap-2 text-sm font-semibold"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                </svg>
                Download {result.filename}
              </button>
            </div>
          )}

          {/* Split button */}
          {!result && (
            <button
              onClick={handleSplit}
              disabled={!ranges.trim() || processing || !!rangeError}
              className="btn-primary w-full text-base py-3.5 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {processing ? (
                <>
                  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Splitting…
                </>
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15" />
                  </svg>
                  Split PDF & Download
                </>
              )}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
